import { RefObject, useRef } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import { NodePricing } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const PRICING_CLUSTER_NODES_PRICING_QUERY_KEY = 'pricing-cluster-nodes-pricing';

const getClusterNodesPricingQueryKey = (clusterId?: string) =>
  [PRICING_CLUSTER_NODES_PRICING_QUERY_KEY, clusterId] as const;

const getClusterNodesPricingQueryOptions = ({
  clusterId,
  refetchInterval,
  enabled,
  syncGroup,
  demoFetchIndexRef,
}: {
  clusterId?: string;
  refetchInterval?: QueryRefetchInterval<NodePricing[] | undefined>;
  enabled: boolean;
  syncGroup?: string;
  demoFetchIndexRef: RefObject<number | undefined>;
}) =>
  queryOptions({
    queryKey: getClusterNodesPricingQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.pricing.getClusterNodesPricing(
        {
          clusterId: clusterId!,
        },
        {
          middlewareArgs: {
            requestMeta: { demoFetchIndex: demoFetchIndexRef.current },
          },
        }
      );
      return data.nodes;
    },
    enabled: !!clusterId && enabled,
    refetchInterval,
    meta: {
      syncGroup: syncGroup,
    },
  });

export const useClusterNodesPricingQuery = (
  clusterId?: string,
  {
    refetchInterval = 20_000,
    enabled = true,
    demoFetchIndex,
    syncGroup,
  }: {
    refetchInterval?: QueryRefetchInterval<NodePricing[] | undefined>;
    enabled?: boolean;
    demoFetchIndex?: number;
    syncGroup?: string;
  } = {}
) => {
  const demoFetchIndexRef = useRef(demoFetchIndex);
  demoFetchIndexRef.current = demoFetchIndex;

  const {
    isLoading,
    data: nodesPricing,
    error,
    refetch,
  } = useQuery(
    getClusterNodesPricingQueryOptions({
      clusterId,
      refetchInterval,
      enabled,
      syncGroup,
      demoFetchIndexRef,
    })
  );

  return { nodesPricing, isLoading, error, refetch };
};
